import type { IndustryContent } from '@/types/content';

export const lawnCareContent: IndustryContent = {
  slug: 'lawn-care',
  name: 'Lawn Care',
  headline: 'The Operating System for Lawn Care Companies',
  subheadline:
    'Manage recurring mowing schedules, optimize routes, and scale your crews—all from one platform built for lawn care pros.',
  heroImageUrl: '/images/industries/lawn-care-hero.webp',
  painPoints: [
    {
      title: 'Recurring schedules are chaos',
      description:
        'Weekly, bi-weekly, every-10-days—tracking who needs service when across hundreds of lawns is a nightmare without the right system.',
      icon: 'calendar-x',
    },
    {
      title: 'Routes waste fuel and time',
      description:
        'Crews zigzag across town instead of working neighborhoods efficiently. Fuel costs and windshield time kill your margins.',
      icon: 'route',
    },
    {
      title: 'Weather disrupts everything',
      description:
        'Rain pushes Monday lawns to Tuesday, which pushes Tuesday to Wednesday. Rescheduling manually takes hours.',
      icon: 'weather',
    },
    {
      title: 'Seasonal scaling is painful',
      description:
        'Spring brings a flood of new customers. Fall means layoffs. Ramping crews up and down every year is exhausting.',
      icon: 'scaling',
    },
    {
      title: 'Upsells slip through the cracks',
      description:
        'Fertilization, aeration, overseeding—you offer the services but forget to pitch them at the right time.',
      icon: 'money',
    },
  ],
  features: [
    {
      title: 'Recurring Service Automation',
      description:
        'Set weekly, bi-weekly, or custom schedules that auto-populate. Handle seasonal adjustments with bulk tools.',
      icon: 'recurring',
    },
    {
      title: 'Route Optimization',
      description:
        'AI-powered routing keeps crews in tight zones. Cut drive time by 30% and fit more lawns per day.',
      icon: 'route',
    },
    {
      title: 'Weather Rescheduling',
      description:
        'Rain in the forecast? Bulk reschedule affected routes and notify customers automatically.',
      icon: 'weather',
    },
    {
      title: 'Crew GPS Tracking',
      description:
        'Know where every crew is in real-time. Track job times, travel time, and productivity.',
      icon: 'gps',
    },
    {
      title: 'Upsell Automation',
      description:
        'Automatically prompt crews to offer aeration in fall, fertilization in spring. Track conversion rates.',
      icon: 'upsell',
    },
    {
      title: 'Customer Portal',
      description:
        'Customers can view schedules, pay invoices, and request additional services online. Fewer phone calls for you.',
      icon: 'portal',
    },
  ],
  testimonial: {
    id: 'lawn-care-1',
    quote:
      'We went from 80 lawns to 200 lawns in one season without adding office staff. The route optimization alone paid for the software.',
    author: 'Mike Patterson',
    role: 'Owner',
    company: 'Patterson Lawn Care',
    industry: 'lawn-care',
    stats: {
      label: 'Growth in One Season',
      value: '150%',
    },
  },
  stats: [
    { label: 'More Lawns Per Day', value: '+30%' },
    { label: 'Fuel Cost Reduction', value: '25%' },
    { label: 'Scheduling Time Saved', value: '80%' },
    { label: 'Customer Retention', value: '92%' },
  ],
  seo: {
    title: 'Lawn Care Software | Scheduling & Route Optimization | Omniforge',
    description:
      'Lawn care software built for growing companies. Route optimization, recurring scheduling, crew tracking, and customer portal. Start your free trial.',
    keywords: [
      'lawn care software',
      'lawn mowing software',
      'lawn care scheduling software',
      'lawn care business software',
      'lawn care route optimization',
    ],
  },
};
